import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  ClipboardList,
  GitMerge,
  Image as ImageIcon,
  Lock,
  Minus,
  Plus,
  Scale,
  Trash2,
  X,
} from 'lucide-react';
import AppShell from '../app/AppShell';
import {
  CustomSelect,
  FeedbackMessage,
  FilterField,
  ModalBackdrop,
  FilterPanel,
  PaginationFooter,
  TableEmptyOverlay,
  ToastStack,
} from '../components/common';
import useReceiving from '../hooks/useReceiving';
import { buildAssetUrl } from '../services/apiConfig';
import {
  createDumpingAdjustment,
  createDumpingLock,
  deleteDumpingAdjustment,
  markDumpingSourceDry,
} from '../services/dumpingAdjustmentService';
import { createPurchaseOrderMerge } from '../services/purchaseOrderService';
import { formatShortDate } from '../utils/formatters';

const initialFilterValues = {
  search: '',
  date: '',
  warehouseLocation: 'All',
};
const INVENTORY_PAGE_SIZE = 10;
const INVENTORY_SIMPLE_PAGE_SIZE = 20;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const INVENTORY_VIEW_MODES = {
  detailed: 'detailed',
  simple: 'simple',
};
const MERGE_SACKS_MARK_OTHER = '__others__';

function DumpingPage({ navigationItems, activePath, onNavigate }) {
  const { inventoryItems, isLoading, loadError, refreshReceivingData } = useReceiving({
    includeUnlockedInventory: true,
    inventoryOnly: true,
  });
  const [filterValues, setFilterValues] = useState(initialFilterValues);
  const [appliedFilters, setAppliedFilters] = useState(initialFilterValues);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState(INVENTORY_VIEW_MODES.detailed);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedInventoryIds, setSelectedInventoryIds] = useState([]);
  const [isMergePreviewOpen, setIsMergePreviewOpen] = useState(false);
  const [isMergeConfirmOpen, setIsMergeConfirmOpen] = useState(false);
  const [isSingleInventoryConfirmOpen, setIsSingleInventoryConfirmOpen] = useState(false);
  const [mergeSacksMarkChoice, setMergeSacksMarkChoice] = useState('');
  const [customMergeSacksMark, setCustomMergeSacksMark] = useState('');
  const [isMerging, setIsMerging] = useState(false);
  const [isSendingSingleInventory, setIsSendingSingleInventory] = useState(false);
  const [inspectionItem, setInspectionItem] = useState(null);
  const [isSavingInspection, setIsSavingInspection] = useState(false);
  const [isLockingInspection, setIsLockingInspection] = useState(false);
  const [isMarkingDryInspection, setIsMarkingDryInspection] = useState(false);
  const [isLockConfirmOpen, setIsLockConfirmOpen] = useState(false);
  const [isMarkDryConfirmOpen, setIsMarkDryConfirmOpen] = useState(false);
  const [inspectionSaveCount, setInspectionSaveCount] = useState(0);
  const [deletingAdjustmentId, setDeletingAdjustmentId] = useState(null);
  const [inspectionError, setInspectionError] = useState('');
  const [mergeError, setMergeError] = useState('');
  const [mergeSuccessMessage, setMergeSuccessMessage] = useState('');
  const [inspectionSuccessMessage, setInspectionSuccessMessage] = useState('');
  const dumpingItems = useMemo(() => getDumpingStageItems(inventoryItems), [inventoryItems]);
  const suggestions = useMemo(() => buildInventorySearchSuggestions(dumpingItems), [dumpingItems]);
  const filteredItems = useMemo(
    () => filterInventoryItems(dumpingItems, appliedFilters),
    [appliedFilters, dumpingItems]
  );
  const supplierStockItems = useMemo(
    () => buildSupplierStockBreakdown(filteredItems),
    [filteredItems]
  );
  const simpleTotalItems = supplierStockItems.length;
  const simpleTotalPages = Math.max(1, Math.ceil(simpleTotalItems / INVENTORY_SIMPLE_PAGE_SIZE));
  const simpleNormalizedPage = Math.min(currentPage, simpleTotalPages);
  const simpleStartIndex = (simpleNormalizedPage - 1) * INVENTORY_SIMPLE_PAGE_SIZE;
  const simpleEndIndex = Math.min(simpleStartIndex + INVENTORY_SIMPLE_PAGE_SIZE, simpleTotalItems);
  const paginatedSupplierStockItems = supplierStockItems.slice(simpleStartIndex, simpleEndIndex);
  const totalItems = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / INVENTORY_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * INVENTORY_PAGE_SIZE;
  const endIndex = Math.min(startIndex + INVENTORY_PAGE_SIZE, totalItems);
  const paginatedItems = filteredItems.slice(startIndex, endIndex);
  const selectedInventoryItems = useMemo(
    () =>
      selectedInventoryIds
        .map((selectedId) =>
          dumpingItems.find(
            (item) => getInventorySelectionId(item) === selectedId
          )
        )
        .filter(Boolean),
    [dumpingItems, selectedInventoryIds]
  );
  const toasts = [
    loadError ? { id: 'dumping-load-error', tone: 'error', message: loadError } : null,
    mergeError ? { id: 'dumping-merge-error', tone: 'error', message: mergeError } : null,
    inspectionError ? { id: 'dumping-inspection-error', tone: 'error', message: inspectionError } : null,
    mergeSuccessMessage
      ? { id: 'dumping-merge-success', tone: 'success', message: mergeSuccessMessage }
      : null,
    inspectionSuccessMessage
      ? { id: 'dumping-inspection-success', tone: 'success', message: inspectionSuccessMessage }
      : null,
  ].filter(Boolean);
  const pagination = {
    currentPage: normalizedPage,
    endItem: endIndex,
    isNextDisabled: normalizedPage >= totalPages,
    isPreviousDisabled: normalizedPage <= 1,
    onNext: () => setCurrentPage((page) => Math.min(page + 1, totalPages)),
    onPrevious: () => setCurrentPage((page) => Math.max(page - 1, 1)),
    startItem: totalItems ? startIndex + 1 : 0,
    totalItems,
    totalPages,
  };
  const simplePagination = {
    currentPage: simpleNormalizedPage,
    endItem: simpleEndIndex,
    isNextDisabled: simpleNormalizedPage >= simpleTotalPages,
    isPreviousDisabled: simpleNormalizedPage <= 1,
    onNext: () => setCurrentPage((page) => Math.min(page + 1, simpleTotalPages)),
    onPrevious: () => setCurrentPage((page) => Math.max(page - 1, 1)),
    startItem: simpleTotalItems ? simpleStartIndex + 1 : 0,
    totalItems: simpleTotalItems,
    totalPages: simpleTotalPages,
  };

  useEffect(() => {
    if (!mergeSuccessMessage && !inspectionSuccessMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setMergeSuccessMessage('');
      setInspectionSuccessMessage('');
    }, 3500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [mergeSuccessMessage, inspectionSuccessMessage]);

  useEffect(() => {
    const options = getMergeSacksMarkOptions(selectedInventoryItems);
    const validValues = new Set(options.map((option) => option.value));

    if (!selectedInventoryItems.length) {
      setMergeSacksMarkChoice('');
      setCustomMergeSacksMark('');
      return;
    }

    if (!mergeSacksMarkChoice || !validValues.has(mergeSacksMarkChoice)) {
      setMergeSacksMarkChoice(options[0]?.value || '');
      setCustomMergeSacksMark('');
    }
  }, [mergeSacksMarkChoice, selectedInventoryItems]);

  function handleFilterChange(event) {
    const { name, value } = event.target;
    setFilterValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));

    if (name === 'search' || name === 'warehouseLocation') {
      setAppliedFilters((currentValues) => ({
        ...currentValues,
        [name]: value,
      }));
      setCurrentPage(1);
    }

  }

  function handleFilterSubmit(event) {
    event.preventDefault();
    setAppliedFilters(filterValues);
    setCurrentPage(1);
  }

  function handleFilterReset() {
    setFilterValues(initialFilterValues);
    setAppliedFilters(initialFilterValues);
    setCurrentPage(1);
  }

  function handleViewModeChange(nextViewMode) {
    setViewMode(nextViewMode);
    setCurrentPage(1);
  }

  function handleToggleInventorySelection(item) {
    const selectionId = getInventorySelectionId(item);
    setMergeError('');
    setMergeSuccessMessage('');
    setInspectionSuccessMessage('');

    setSelectedInventoryIds((currentIds) => {
      if (currentIds.includes(selectionId)) {
        return currentIds.filter((currentId) => currentId !== selectionId);
      }

      return [...currentIds, selectionId];
    });
  }

  function handleClearMergeSelection() {
    setSelectedInventoryIds([]);
    setIsMergePreviewOpen(false);
    setIsMergeConfirmOpen(false);
    setIsSingleInventoryConfirmOpen(false);
    setMergeSacksMarkChoice('');
    setCustomMergeSacksMark('');
    setMergeError('');
  }

  async function handleSubmitMerge() {
    const mergedSacksMark = getSelectedMergeSacksMark(
      mergeSacksMarkChoice,
      customMergeSacksMark
    );

    if (!mergedSacksMark) {
      setMergeError('Select or enter a sacks mark for the merged dumping batch.');
      return;
    }

    setIsMergeConfirmOpen(false);
    setIsMerging(true);
    setMergeError('');
    setMergeSuccessMessage('');

    try {
      const createdMerge = await createPurchaseOrderMerge({
        mergedSacksMark,
        mergedSacksMarkSource:
          mergeSacksMarkChoice === MERGE_SACKS_MARK_OTHER ? 'custom' : 'selected',
        sources: selectedInventoryItems.map((item) => ({
          sourceType: getDumpingSourceType(item),
          sourceId: getDumpingSourceId(item),
        })),
      });

      await refreshReceivingData(false);
      setMergeSuccessMessage(`${createdMerge.mergeCode} was created from ${selectedInventoryItems.length} P.O.s.`);
      setSelectedInventoryIds([]);
      setIsMergePreviewOpen(false);
      setMergeSacksMarkChoice('');
      setCustomMergeSacksMark('');
    } catch (error) {
      setMergeError(error.message);
    } finally {
      setIsMerging(false);
    }
  }

  async function handleSendSingleToInventory() {
    const selectedItem = selectedInventoryItems[0];

    if (!selectedItem) {
      return;
    }

    setIsSendingSingleInventory(true);
    setMergeError('');
    setMergeSuccessMessage('');

    try {
      await createDumpingLock({
        sourceType: getDumpingSourceType(selectedItem),
        sourceId: getDumpingSourceId(selectedItem),
      });
      await refreshReceivingData(false);
      setMergeSuccessMessage(`${formatPurchaseOrderCode(selectedItem)} was sent to Inventory.`);
      setSelectedInventoryIds([]);
      setIsSingleInventoryConfirmOpen(false);
    } catch (error) {
      setMergeError(error.message);
    } finally {
      setIsSendingSingleInventory(false);
    }
  }

  async function handleSubmitInspection(payload) {
    setIsSavingInspection(true);
    setInspectionError('');
    setInspectionSuccessMessage('');

    try {
      await createDumpingAdjustment(payload);
      const nextInventoryItems = await refreshReceivingData(false);
      setInspectionSuccessMessage('Dumping adjustment was saved.');
      setInspectionSaveCount((count) => count + 1);
      setInspectionItem((currentItem) => refreshInspectionItem(currentItem, nextInventoryItems));
    } catch (error) {
      setInspectionError(error.message);
    } finally {
      setIsSavingInspection(false);
    }
  }

  async function handleDeleteInspectionAdjustment(adjustmentId) {
    setDeletingAdjustmentId(adjustmentId);
    setInspectionError('');
    setInspectionSuccessMessage('');

    try {
      await deleteDumpingAdjustment(adjustmentId);
      const nextInventoryItems = await refreshReceivingData(false);
      setInspectionSuccessMessage('Dumping adjustment was deleted.');
      setInspectionItem((currentItem) => refreshInspectionItem(currentItem, nextInventoryItems));
    } catch (error) {
      setInspectionError(error.message);
    } finally {
      setDeletingAdjustmentId(null);
    }
  }

  async function handleLockInspectionItem() {
    if (!inspectionItem) {
      return;
    }

    setIsLockingInspection(true);
    setInspectionError('');
    setInspectionSuccessMessage('');

    try {
      await createDumpingLock({
        sourceType: getDumpingSourceType(inspectionItem),
        sourceId: getDumpingSourceId(inspectionItem),
      });
      await refreshReceivingData(false);
      setInspectionSuccessMessage('Dumping inventory was locked.');
      setInspectionItem(null);
      setIsLockConfirmOpen(false);
    } catch (error) {
      setInspectionError(error.message);
    } finally {
      setIsLockingInspection(false);
    }
  }

  async function handleMarkInspectionItemDry() {
    if (!inspectionItem) {
      return;
    }

    setIsMarkingDryInspection(true);
    setInspectionError('');
    setInspectionSuccessMessage('');

    try {
      await markDumpingSourceDry({
        sourceType: getDumpingSourceType(inspectionItem),
        sourceId: getDumpingSourceId(inspectionItem),
      });
      const nextInventoryItems = await refreshReceivingData(false);
      setInspectionSuccessMessage('Wet batch was marked as dry and can now be merged.');
      setInspectionItem((currentItem) => refreshInspectionItem(currentItem, nextInventoryItems));
      setSelectedInventoryIds([]);
      setIsMarkDryConfirmOpen(false);
    } catch (error) {
      setInspectionError(error.message);
    } finally {
      setIsMarkingDryInspection(false);
    }
  }

  return (
    <AppShell
      navigationItems={navigationItems}
      activePath={activePath}
      onNavigate={onNavigate}
    >
      <ToastStack toasts={toasts} />
      <section className="mt-5 px-4 sm:mt-6 sm:px-6 lg:px-[26px]">
        <div className="grid gap-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="m-0 text-[1.25rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.4rem]">
                Dumping
              </h2>
              <p className="m-0 mt-2 text-[0.96rem] text-[var(--theme-text-muted)]">
                Inspect received seaweeds, review source details, and merge compatible P.O.s before final inventory review.
              </p>
            </div>
            <InventoryViewSwitch value={viewMode} onChange={handleViewModeChange} />
          </div>

          <InventoryFilters
            values={filterValues}
            suggestions={suggestions}
            warehouseOptions={buildWarehouseOptions(inventoryItems)}
            onChange={handleFilterChange}
            onSubmit={handleFilterSubmit}
            onReset={handleFilterReset}
          />

          {isLoading ? (
            <FeedbackMessage tone="info" message="Loading records for dumping..." />
          ) : (
            <div className="grid gap-3">
              {viewMode === INVENTORY_VIEW_MODES.simple ? (
                <InventorySimpleView items={paginatedSupplierStockItems} />
              ) : (
                <>
                  <InventoryMergeToolbar
                    selectedItems={selectedInventoryItems}
                    isSendingSingleInventory={isSendingSingleInventory}
                    onClear={handleClearMergeSelection}
                    onOpenPreview={() => setIsMergePreviewOpen(true)}
                    onOpenSingleInventoryConfirm={() => setIsSingleInventoryConfirmOpen(true)}
                  />
                  <InventoryTable
                    items={paginatedItems}
                    selectedItems={selectedInventoryItems}
                    selectedInventoryIds={selectedInventoryIds}
                    onPreviewImage={setPreviewImage}
                    onToggleSelection={handleToggleInventorySelection}
                  />
                </>
              )}
              {viewMode === INVENTORY_VIEW_MODES.detailed && totalItems ? (
                <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
                  <PaginationFooter {...pagination} />
                </div>
              ) : null}
              {viewMode === INVENTORY_VIEW_MODES.simple &&
              simpleTotalItems > INVENTORY_SIMPLE_PAGE_SIZE ? (
                <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
                  <PaginationFooter {...simplePagination} />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </section>

      {previewImage ? (
        <ReceiptImagePreviewModal
          image={previewImage}
          onClose={() => setPreviewImage(null)}
        />
      ) : null}
      {isMergePreviewOpen ? (
        <MergePreviewModal
          items={selectedInventoryItems}
          sacksMarkChoice={mergeSacksMarkChoice}
          customSacksMark={customMergeSacksMark}
          onSacksMarkChoiceChange={setMergeSacksMarkChoice}
          onCustomSacksMarkChange={setCustomMergeSacksMark}
          isMerging={isMerging}
          onClose={() => setIsMergePreviewOpen(false)}
          onSubmit={() => setIsMergeConfirmOpen(true)}
        />
      ) : null}
      {isMergeConfirmOpen ? (
        <MergeSubmitConfirmationModal
          items={selectedInventoryItems}
          mergedSacksMark={getSelectedMergeSacksMark(
            mergeSacksMarkChoice,
            customMergeSacksMark
          )}
          isSubmitting={isMerging}
          onCancel={() => setIsMergeConfirmOpen(false)}
          onConfirm={handleSubmitMerge}
        />
      ) : null}
      {isSingleInventoryConfirmOpen && selectedInventoryItems[0] ? (
        <SendSingleInventoryConfirmationModal
          item={selectedInventoryItems[0]}
          isSubmitting={isSendingSingleInventory}
          onCancel={() => setIsSingleInventoryConfirmOpen(false)}
          onConfirm={handleSendSingleToInventory}
        />
      ) : null}
      {inspectionItem ? (
        <DumpingInspectionModal
          item={inspectionItem}
          isSubmitting={isSavingInspection}
          isLocking={isLockingInspection}
          saveCount={inspectionSaveCount}
          onCancel={() => {
            setInspectionItem(null);
            setInspectionError('');
            setIsLockConfirmOpen(false);
            setIsMarkDryConfirmOpen(false);
          }}
          deletingAdjustmentId={deletingAdjustmentId}
          onDeleteAdjustment={handleDeleteInspectionAdjustment}
          onOpenLockConfirm={() => setIsLockConfirmOpen(true)}
          isMarkingDry={isMarkingDryInspection}
          onOpenMarkDryConfirm={() => setIsMarkDryConfirmOpen(true)}
          onSubmit={handleSubmitInspection}
        />
      ) : null}
      {isMarkDryConfirmOpen && inspectionItem ? (
        <MarkDryConfirmationModal
          item={inspectionItem}
          isSubmitting={isMarkingDryInspection}
          onCancel={() => setIsMarkDryConfirmOpen(false)}
          onConfirm={handleMarkInspectionItemDry}
        />
      ) : null}
      {isLockConfirmOpen && inspectionItem ? (
        <DumpingLockConfirmationModal
          item={inspectionItem}
          isSubmitting={isLockingInspection}
          onCancel={() => setIsLockConfirmOpen(false)}
          onConfirm={handleLockInspectionItem}
        />
      ) : null}
    </AppShell>
  );
}

function InventoryViewSwitch({ value, onChange }) {
  return (
    <div className="inline-flex w-fit rounded-full border border-[var(--theme-border)] bg-white p-1 shadow-[0_10px_24px_rgba(var(--theme-shadow-rgb),0.06)]">
      {[
        { value: INVENTORY_VIEW_MODES.detailed, label: 'Detailed' },
        { value: INVENTORY_VIEW_MODES.simple, label: 'Simple' },
      ].map((option) => {
        const isActive = value === option.value;

        return (
          <button
            key={option.value}
            className={`min-h-[38px] rounded-full px-4 text-[0.84rem] font-semibold transition ${
              isActive
                ? 'bg-[var(--theme-accent-panel)] text-[var(--theme-accent-panel-text)] shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.12)]'
                : 'text-[var(--theme-text-muted)] hover:bg-[var(--theme-surface-soft)] hover:text-[var(--theme-text-strong)]'
            }`}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={isActive}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function InventoryFilters({ values, suggestions, warehouseOptions = [], onChange, onSubmit, onReset }) {
  return (
    <FilterPanel
      onSubmit={onSubmit}
      onReset={onReset}
      layoutClassName="grid gap-2.5 md:grid-cols-2 xl:grid-cols-[minmax(300px,1.2fr)_220px_220px_190px]"
    >
      <FilterField
        label="Search"
        name="search"
        type="search"
        value={values.search}
        onChange={onChange}
        placeholder="Search P.O. code, supplier, warehouse, remarks"
        suggestions={suggestions}
      />
      <FilterField
        label="Warehouse"
        name="warehouseLocation"
        value={values.warehouseLocation}
        onChange={onChange}
        options={warehouseOptions}
      />
      <FilterField
        label="Received Date"
        name="date"
        type="date"
        value={values.date}
        onChange={onChange}
      />
    </FilterPanel>
  );
}

function InventorySimpleView({ items }) {
  return (
    <section className="rounded-[22px] border border-[var(--theme-border)] bg-white p-4 shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)] sm:p-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="m-0 text-[0.82rem] text-[var(--theme-text-muted)] sm:text-sm">
            Wet stock breakdown
          </p>
          <strong className="mt-1 block text-[1.25rem] font-semibold text-[var(--theme-text)]">
            Seaweeds Stocks by Sacks Mark
          </strong>
        </div>
        <span className="rounded-full border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-3 py-1 text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-subtle)]">
          Filtered Inventory
        </span>
      </div>

      {items.length ? (
        <div className="grid gap-2">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between gap-4 rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] px-4 py-3"
            >
              <span className="min-w-0 truncate text-[0.94rem] font-medium text-[var(--theme-text-strong)]">
                {item.sacksMark}
              </span>
              <div className="flex shrink-0 flex-wrap justify-end gap-2 text-right">
                <span className="rounded-full border border-[var(--theme-border)] bg-white px-3 py-1 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)]">
                  {formatQuantity(item.quantity, item.quantityUnit)}
                </span>
                <strong className="rounded-full border border-[var(--theme-border)] bg-[var(--theme-accent-soft)] px-3 py-1 text-[0.88rem] font-semibold text-[var(--theme-text)]">
                  {formatKgNet(item.quantityKg)}
                </strong>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-[18px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-5 py-12">
          <EmptyState />
        </div>
      )}
    </section>
  );
}

function InventoryMergeToolbar({
  selectedItems,
  isSendingSingleInventory,
  onClear,
  onOpenPreview,
  onOpenSingleInventoryConfirm,
}) {
  const selectedCount = selectedItems.length;
  const canMerge = selectedCount >= 2;
  const canSendSingle = selectedCount === 1;
  const summary = selectedItems.length
    ? buildMergedInventorySummary(selectedItems)
    : null;

  if (!selectedCount) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-[18px] border border-[var(--theme-border)] bg-white px-4 py-3 shadow-[0_12px_24px_rgba(var(--theme-shadow-rgb),0.07)]">
      <div>
        <strong className="block text-[0.95rem] text-[var(--theme-text-strong)]">
          {selectedCount} inventory {selectedCount === 1 ? 'record' : 'records'} selected
        </strong>
        <span className="mt-1 block text-[0.78rem] text-[var(--theme-text-muted)]">
          {summary
            ? `${summary.warehouseLabel} - ${summary.quantityBasisLabel} - ${formatKgNet(summary.totalKgNet)}`
            : 'Select at least two compatible records to preview a merge.'}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          className="inline-flex min-h-[38px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-4 text-[0.84rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
          type="button"
          onClick={onClear}
        >
          Clear
        </button>
        {canSendSingle ? (
          <button
            className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-full border border-[var(--theme-accent)] bg-[var(--theme-accent)] px-4 text-[0.84rem] font-semibold text-white shadow-[0_10px_20px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
            type="button"
            onClick={onOpenSingleInventoryConfirm}
            disabled={isSendingSingleInventory}
          >
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            {isSendingSingleInventory ? 'Sending...' : 'Send to Inventory'}
          </button>
        ) : (
          <button
            className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-full border border-[var(--theme-accent)] bg-[var(--theme-accent)] px-4 text-[0.84rem] font-semibold text-white shadow-[0_10px_20px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
            type="button"
            onClick={onOpenPreview}
            disabled={!canMerge}
          >
            <GitMerge className="h-4 w-4" aria-hidden="true" />
            Preview Merge
          </button>
        )}
      </div>
    </div>
  );
}

function InventoryTable({
  items,
  selectedItems,
  selectedInventoryIds,
  onPreviewImage,
  onToggleSelection,
}) {
  return (
    <div className="relative overflow-hidden rounded-[22px] border border-[var(--theme-border)] bg-white shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)]">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[1120px] border-collapse">
          <thead>
            <tr className="bg-[var(--theme-accent-panel)]">
              <TableHeading>Merge</TableHeading>
              <TableHeading>P.O. Code</TableHeading>
              <TableHeading>Sacks Mark</TableHeading>
              <TableHeading>Warehouse</TableHeading>
              <TableHeading>Received Date</TableHeading>
              <TableHeading>Sacks</TableHeading>
              <TableHeading>MC %</TableHeading>
              <TableHeading>KG Net</TableHeading>
              <TableHeading>Details</TableHeading>
            </tr>
          </thead>
          <tbody>
            {items.length ? (
              items.map((item) => (
                <InventoryRow
                  key={item.id}
                  item={item}
                  mergeEligibility={getInventoryMergeEligibility(item, selectedItems)}
                  isSelected={selectedInventoryIds.includes(getInventorySelectionId(item))}
                  onPreviewImage={onPreviewImage}
                  onToggleSelection={onToggleSelection}
                />
              ))
            ) : (
              <TableEmptyOverlay.Row colSpan={9} />
            )}
          </tbody>
        </table>
      </div>
      {!items.length ? (
        <TableEmptyOverlay headerOffsetClassName="top-[48px]">
          <EmptyState />
        </TableEmptyOverlay>
      ) : null}

      <div className="grid gap-3 p-4 md:hidden">
        {items.length ? (
          items.map((item) => (
            <InventoryCard
              key={item.id}
              item={item}
              mergeEligibility={getInventoryMergeEligibility(item, selectedItems)}
              isSelected={selectedInventoryIds.includes(getInventorySelectionId(item))}
              onPreviewImage={onPreviewImage}
              onToggleSelection={onToggleSelection}
            />
          ))
        ) : (
          <div className="rounded-[18px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-5 py-12">
            <EmptyState />
          </div>
        )}
      </div>
    </div>
  );
}

function InventoryRow({
  item,
  mergeEligibility,
  isSelected,
  onPreviewImage,
  onToggleSelection,
}) {
  const note = splitReceivingNote(item.receiptDetailsSourceRemarks || item.remarks);

  return (
    <tr className="border-t border-[var(--theme-border-soft)] transition-colors hover:bg-[var(--theme-surface-soft)]">
      <TableCell>
        <MergeSelectionCheckbox
          item={item}
          isSelected={isSelected}
          mergeEligibility={mergeEligibility}
          onToggleSelection={onToggleSelection}
        />
      </TableCell>
      <TableCell>{formatPurchaseOrderCode(item)}</TableCell>
      <TableCell>
        <span
          className="block max-w-[180px] truncate"
          title={formatSacksMarkDisplay(item)}
        >
          {formatSacksMarkDisplay(item)}
        </span>
      </TableCell>
      <TableCell>
        <span className="block max-w-[150px] truncate" title={formatWarehouseLocation(item)}>
          {formatWarehouseLocation(item)}
        </span>
      </TableCell>
      <TableCell>{formatShortDate(item.receivedAt)}</TableCell>
      <TableCell>{formatInventoryQuantity(item, 'quantity')}</TableCell>
      <TableCell>{formatMoistureContent(item.moistureContent)}</TableCell>
      <TableCell>
        <strong className="font-semibold text-[#246067]">
          {formatKgNet(item.quantityKg)}
        </strong>
      </TableCell>
      <TableCell>
        {item.isMergedInventory || note.receiptDetails || item.image ? (
          <ReceiptDetails
            details={note.receiptDetails}
            imagePath={item.image}
            item={item}
            remarks={note.remarks}
            onPreviewImage={onPreviewImage}
            title={formatPurchaseOrderCode(item)}
          />
        ) : '-'}
      </TableCell>
    </tr>
  );
}

function InventoryCard({
  item,
  mergeEligibility,
  isSelected,
  onPreviewImage,
  onToggleSelection,
}) {
  const note = splitReceivingNote(item.receiptDetailsSourceRemarks || item.remarks);

  return (
    <article className="rounded-[18px] border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] p-3 transition duration-150 hover:-translate-y-0.5 hover:bg-[var(--theme-surface-soft)] hover:shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.11)]">
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <MergeSelectionCheckbox
            item={item}
            isSelected={isSelected}
            mergeEligibility={mergeEligibility}
            onToggleSelection={onToggleSelection}
          />
          <div className="min-w-0">
          <strong className="block text-[1rem] text-[var(--theme-text)]">
            {formatPurchaseOrderCode(item)}
          </strong>
          <span className="mt-1 block text-[0.92rem] text-[var(--theme-text-muted)]">
            {formatSacksMarkDisplay(item)}
          </span>
          </div>
        </div>
      </div>

      <div className="mt-3 grid gap-1.5 text-[0.88rem] text-[var(--theme-text-subtle)]">
        <RecordMeta label="Received Date" value={formatShortDate(item.receivedAt)} />
        <RecordMeta label="Warehouse" value={formatWarehouseLocation(item)} />
        <RecordMeta label="MC %" value={formatMoistureContent(item.moistureContent)} />
        <RecordMeta label="Sacks" value={formatInventoryQuantity(item, 'quantity')} />
        <RecordMeta label="KG Net" value={formatKgNet(item.quantityKg)} />
        <RecordMeta
          label="Receipt Details"
          value={
            item.isMergedInventory || note.receiptDetails || item.image ? (
              <ReceiptDetails
                details={note.receiptDetails}
                imagePath={item.image}
                item={item}
                remarks={note.remarks}
                onPreviewImage={onPreviewImage}
                title={formatPurchaseOrderCode(item)}
              />
            ) : '-'
          }
        />
      </div>
    </article>
  );
}

function MergeSelectionCheckbox({ item, isSelected, mergeEligibility, onToggleSelection }) {
  const purchaseOrderCode = formatPurchaseOrderCode(item);
  const isDisabled = !isSelected && !mergeEligibility.canSelect;

  return (
    <label
      className="inline-flex items-center"
      title={isDisabled ? mergeEligibility.reason : `Select ${purchaseOrderCode} for merge`}
    >
      <input
        className="h-4 w-4 rounded border-[var(--theme-border)] text-[var(--theme-accent)] focus:ring-[var(--theme-accent)] disabled:cursor-not-allowed"
        type="checkbox"
        checked={isSelected}
        disabled={isDisabled}
        onChange={() => onToggleSelection(item)}
        aria-label={
          isDisabled
            ? `${purchaseOrderCode} cannot be merged. ${mergeEligibility.reason}`
            : `Select ${purchaseOrderCode} for merge`
        }
      />
      {isDisabled ? (
        <span className="ml-2 text-[0.68rem] font-semibold text-[#b45f45]">
          {mergeEligibility.shortReason}
        </span>
      ) : null}
    </label>
  );
}

function DumpingLockBadge({ item }) {
  if (!item.isDumpingLocked) {
    return (
      <span className="inline-flex min-h-[30px] items-center justify-center rounded-full border border-[var(--theme-border-soft)] bg-white px-3 text-[0.72rem] font-semibold text-[var(--theme-text-muted)]">
        Open
      </span>
    );
  }

  return (
    <span
      className="inline-flex min-h-[30px] items-center justify-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-[var(--theme-accent-soft)] px-3 text-[0.72rem] font-semibold text-[var(--theme-text-subtle)]"
      title={item.dumpingLock?.lockedByName ? `Locked by ${item.dumpingLock.lockedByName}` : 'Locked'}
    >
      <Lock className="h-3.5 w-3.5" aria-hidden="true" />
      Locked
    </span>
  );
}

function ReceiptDetails({ details, imagePath, item, remarks, onPreviewImage, title }) {
  const imageUrl = buildAssetUrl(imagePath);

  return (
    <button
      className="inline-flex min-h-[36px] items-center justify-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-white px-3 py-1.5 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)]"
      type="button"
      onClick={() => onPreviewImage({ url: imageUrl, title, details, item, remarks })}
    >
      <ImageIcon className="h-3.5 w-3.5" aria-hidden="true" />
      {item?.isMergedInventory ? 'Review merge' : 'View details'}
    </button>
  );
}

function MergePreviewModal({
  items,
  sacksMarkChoice,
  customSacksMark,
  onSacksMarkChoiceChange,
  onCustomSacksMarkChange,
  isMerging,
  onClose,
  onSubmit,
}) {
  const summary = buildMergedInventorySummary(items);
  const sacksMarkOptions = getMergeSacksMarkOptions(items);
  const selectedSacksMark = getSelectedMergeSacksMark(sacksMarkChoice, customSacksMark);
  const isCustomSacksMark = sacksMarkChoice === MERGE_SACKS_MARK_OTHER;
  const canSubmit = items.length >= 2 && Boolean(selectedSacksMark);

  if (!summary) {
    return null;
  }

  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.55)]"
      contentClassName="!px-4"
      zIndexClassName="z-50"
    >
      <div className="mx-auto w-full max-w-5xl rounded-[22px] border border-[var(--theme-border)] bg-white p-4 shadow-[0_24px_70px_rgba(var(--theme-shadow-rgb),0.32)] sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <strong className="text-[1.02rem] text-[var(--theme-text)]">
              Merge Preview
            </strong>
            <p className="m-0 mt-1 text-[0.84rem] text-[var(--theme-text-muted)]">
              Original P.O.s remain unchanged. This preview shows the consolidated inventory values.
            </p>
          </div>
          <button
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onClose}
            aria-label="Close merge preview"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <MergeSummaryTile label="Warehouse" value={summary.warehouseLabel} />
          <MergeSummaryTile label="Sacks Mark" value={summary.supplierLabel} />
          <MergeSummaryTile label="MC %" value={summary.moistureLabel} />
          <MergeSummaryTile label="Total Quantity" value={summary.totalQuantityLabel} />
          <MergeSummaryTile label="KG Net" value={formatKgNet(summary.totalKgNet)} />
        </div>

        <div className="mt-4 rounded-[16px] border border-[var(--theme-border)] bg-[var(--theme-app-bg)] p-3">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <CustomSelect
              label="New Sacks Mark"
              name="mergedSacksMark"
              value={sacksMarkChoice}
              onChange={(event) => onSacksMarkChoiceChange(event.target.value)}
              options={sacksMarkOptions}
              placeholder="Select sacks mark"
            />
            {isCustomSacksMark ? (
              <label className="grid gap-1.5">
                <span className="text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">
                  Custom Sacks Mark
                </span>
                <input
                  className="min-h-[40px] rounded-full border border-[var(--theme-border)] bg-white px-3 py-2 text-[0.84rem] outline-none transition placeholder:text-[var(--theme-text-muted)] focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[var(--theme-accent-soft)]"
                  type="text"
                  value={customSacksMark}
                  onChange={(event) => onCustomSacksMarkChange(event.target.value.toUpperCase())}
                  placeholder="Enter new sacks mark"
                  maxLength={120}
                />
              </label>
            ) : (
              <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white px-3 py-2 text-[0.78rem] leading-5 text-[var(--theme-text-muted)]">
                The selected mark becomes the merged batch identity. Source P.O. marks remain unchanged.
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-[16px] border border-[var(--theme-border-soft)]">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse">
              <thead>
                <tr className="bg-[var(--theme-accent-panel)]">
                  <TableHeading>Source P.O.</TableHeading>
                  <TableHeading>Sacks Mark</TableHeading>
                  <TableHeading>Sacks</TableHeading>
                  <TableHeading>MC %</TableHeading>
                  <TableHeading>KG Net</TableHeading>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={getInventorySelectionId(item)} className="border-t border-[var(--theme-border-soft)]">
                    <TableCell>{formatPurchaseOrderCode(item)}</TableCell>
                    <TableCell>
                      <span
                        className="block max-w-[190px] truncate"
                        title={formatSacksMarkDisplay(item)}
                      >
                        {formatSacksMarkDisplay(item)}
                      </span>
                    </TableCell>
                    <TableCell>{formatInventoryQuantity(item, 'quantity')}</TableCell>
                    <TableCell>{formatMoistureContent(item.moistureContent)}</TableCell>
                    <TableCell>{formatKgNet(item.quantityKg)}</TableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 rounded-[16px] border border-[var(--theme-border)] bg-[var(--theme-surface-soft)] p-3 text-[0.84rem] leading-5 text-[var(--theme-text-subtle)]">
          Balancing amounts stay locked. This merge only combines selected wet lots from the same warehouse into one sacks mark.
        </div>

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-5 text-[0.86rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onClose}
            disabled={isMerging}
          >
            Review More
          </button>
          <button
            className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-full border border-[var(--theme-accent)] bg-[var(--theme-accent)] px-5 text-[0.86rem] font-semibold text-white shadow-[0_10px_20px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
            type="button"
            onClick={onSubmit}
            disabled={isMerging || !canSubmit}
          >
            <GitMerge className="h-4 w-4" aria-hidden="true" />
            {isMerging ? 'Creating Merge...' : 'Submit Merge'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function MergeSubmitConfirmationModal({ items, mergedSacksMark, isSubmitting, onCancel, onConfirm }) {
  const summary = buildMergedInventorySummary(items);

  if (!summary) {
    return null;
  }

  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.62)]"
      contentClassName="!items-center"
      zIndexClassName="z-[60]"
    >
      <div className="mx-auto w-full max-w-lg rounded-[20px] border border-[var(--theme-border)] bg-white p-5 shadow-[0_24px_70px_rgba(var(--theme-shadow-rgb),0.34)]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <strong className="block text-[1rem] text-[var(--theme-text)]">
              Submit merge?
            </strong>
            <p className="m-0 mt-2 text-[0.86rem] leading-5 text-[var(--theme-text-subtle)]">
              This will create a merged inventory record and hide the selected source P.O.s from the available inventory table.
            </p>
          </div>
          <button
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Cancel merge confirmation"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-4 grid gap-2 rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3 text-[0.84rem] text-[var(--theme-text-subtle)]">
          <CalculationLine label="Source P.O.s" value={String(items.length)} />
          <CalculationLine label="New Sacks Mark" value={mergedSacksMark || '-'} />
          <CalculationLine label="Warehouse" value={summary.warehouseLabel} />
          <CalculationLine label="Total Quantity" value={summary.totalQuantityLabel} />
          <CalculationLine label="KG Net" value={formatKgNet(summary.totalKgNet)} />
        </div>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-5 text-[0.86rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-full border border-[var(--theme-accent)] bg-[var(--theme-accent)] px-5 text-[0.86rem] font-semibold text-white shadow-[0_10px_20px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            <GitMerge className="h-4 w-4" aria-hidden="true" />
            {isSubmitting ? 'Submitting...' : 'Confirm Merge'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function SendSingleInventoryConfirmationModal({ item, isSubmitting, onCancel, onConfirm }) {
  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.62)]"
      contentClassName="!items-center"
      zIndexClassName="z-[60]"
    >
      <div className="mx-auto w-full max-w-lg rounded-[20px] border border-[var(--theme-border)] bg-white p-5 shadow-[0_24px_70px_rgba(var(--theme-shadow-rgb),0.34)]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <strong className="block text-[1rem] text-[var(--theme-text)]">
              Send wet lot to Inventory?
            </strong>
            <p className="m-0 mt-2 text-[0.86rem] leading-5 text-[var(--theme-text-subtle)]">
              This will finalize {formatPurchaseOrderCode(item)} as inventory stock and remove it from Dumping.
            </p>
          </div>
          <button
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Cancel send to inventory confirmation"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-4 grid gap-2 rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3 text-[0.84rem] text-[var(--theme-text-subtle)]">
          <CalculationLine label="Source P.O." value={formatPurchaseOrderCode(item)} />
          <CalculationLine label="Warehouse" value={formatWarehouseLocation(item)} />
          <CalculationLine label="Sacks" value={formatInventoryQuantity(item, 'quantity')} />
          <CalculationLine label="KG Net" value={formatKgNet(item.quantityKg)} />
        </div>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-5 text-[0.86rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-full border border-[var(--theme-accent)] bg-[var(--theme-accent)] px-5 text-[0.86rem] font-semibold text-white shadow-[0_10px_20px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            {isSubmitting ? 'Sending...' : 'Confirm Send'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

const DUMPING_ADJUSTMENT_REASONS = [
  'Salty',
  'Sandy',
  'Damaged',
  'Spoiled',
  'Contaminated',
  'Rejected',
  'Underweight Adjustment',
  'Overweight Adjustment',
  'Other',
];
const DUMPING_ADJUSTMENT_REASON_OPTIONS = DUMPING_ADJUSTMENT_REASONS.map((value) => ({
  value,
  label: value,
}));
const DUMPING_ADJUSTMENTS_PAGE_SIZE = 5;

function DumpingInspectionModal({
  item,
  isSubmitting,
  isLocking,
  isMarkingDry,
  saveCount,
  deletingAdjustmentId,
  onCancel,
  onDeleteAdjustment,
  onOpenLockConfirm,
  onOpenMarkDryConfirm,
  onSubmit,
}) {
  const [reason, setReason] = useState(DUMPING_ADJUSTMENT_REASONS[0]);
  const [quantityKg, setQuantityKg] = useState('');
  const [quantitySacks, setQuantitySacks] = useState('');
  const [notes, setNotes] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  const [adjustmentsPage, setAdjustmentsPage] = useState(1);
  const dumpingAdjustments = item.dumpingAdjustments || [];
  const isLocked = Boolean(item.isDumpingLocked);
  const isMergedInventory = Boolean(item.isMergedInventory);
  const canAdjustInventory = !isMergedInventory;
  const adjustmentsTotalPages = Math.max(
    1,
    Math.ceil(dumpingAdjustments.length / DUMPING_ADJUSTMENTS_PAGE_SIZE)
  );
  const normalizedAdjustmentsPage = Math.min(adjustmentsPage, adjustmentsTotalPages);
  const shouldPaginateAdjustments = dumpingAdjustments.length > DUMPING_ADJUSTMENTS_PAGE_SIZE;
  const visibleDumpingAdjustments = dumpingAdjustments.slice(
    (normalizedAdjustmentsPage - 1) * DUMPING_ADJUSTMENTS_PAGE_SIZE,
    normalizedAdjustmentsPage * DUMPING_ADJUSTMENTS_PAGE_SIZE
  );
  const availableKg = Number(item.quantityKg || 0);
  const existingAdjustmentKg = Number(item.dumpingAdjustmentKg || 0);
  const existingAdjustmentSacks = Number(item.dumpingAdjustmentSacks || 0);
  const originalSacks = Number(getOriginalQuantity(item) || 0);
  const canShowSacksDetails =
    item.quantityUnit === 'sacks' ||
    existingAdjustmentSacks > 0 ||
    (item.isMergedInventory && originalSacks > 0);
  const availableSacks = canShowSacksDetails
    ? Math.max(0, Number((originalSacks - existingAdjustmentSacks).toFixed(2)))
    : 0;
  const canAdjustSacks = item.quantityUnit === 'sacks' && !item.isMergedInventory;
  const shouldShowSacksInput = canAdjustSacks || canShowSacksDetails;
  const canMarkDry =
    !isLocked &&
    getDumpingSourceType(item) === 'receiving' &&
    String(item.category || '').toLowerCase() === 'wet';

  useEffect(() => {
    setAdjustmentsPage(1);
  }, [item.id]);

  useEffect(() => {
    if (adjustmentsPage > adjustmentsTotalPages) {
      setAdjustmentsPage(adjustmentsTotalPages);
    }
  }, [adjustmentsPage, adjustmentsTotalPages]);

  useEffect(() => {
    if (saveCount <= 0) {
      return;
    }

    setQuantityKg('');
    setQuantitySacks('');
    setNotes('');
    setValidationMessage('');
    setAdjustmentsPage(1);
  }, [saveCount]);

  function handleSubmit(event) {
    event.preventDefault();

    if (isLocked) {
      setValidationMessage('This inventory record is locked from Dumping.');
      return;
    }

    if (!canAdjustInventory) {
      setValidationMessage(
        'Merged inventory cannot be adjusted in Dumping. Please apply dumping adjustments to the source P.O.s before merging.'
      );
      return;
    }

    const numericQuantityKg = Number(quantityKg);
    const numericQuantitySacks = Number(quantitySacks);

    if (!Number.isFinite(numericQuantityKg) || numericQuantityKg < 0) {
      setValidationMessage('Adjustment KG must be zero or greater.');
      return;
    }

    if (!Number.isFinite(numericQuantitySacks) || numericQuantitySacks < 0) {
      setValidationMessage('Adjustment sacks must be zero or greater.');
      return;
    }

    if (numericQuantitySacks > 0 && !Number.isInteger(numericQuantitySacks)) {
      setValidationMessage('Adjustment sacks must be a whole number.');
      return;
    }

    if (numericQuantityKg <= 0 && numericQuantitySacks <= 0) {
      setValidationMessage('Enter an adjustment KG or sacks greater than zero.');
      return;
    }

    if (reason !== 'Overweight Adjustment' && numericQuantityKg > availableKg) {
      setValidationMessage(`Adjustment cannot exceed available ${formatKgNet(availableKg)}.`);
      return;
    }

    if (reason !== 'Overweight Adjustment' && numericQuantitySacks > availableSacks) {
      setValidationMessage(
        `Adjustment cannot exceed available ${formatQuantity(availableSacks, 'sacks')}.`
      );
      return;
    }

    setValidationMessage('');
    onSubmit({
      sourceType: getDumpingSourceType(item),
      sourceId: getDumpingSourceId(item),
      reason,
      quantityKg: numericQuantityKg,
      quantitySacks: canAdjustSacks ? numericQuantitySacks : 0,
      notes,
    });
  }

  function handleQuantityChange(event) {
    const nextValue = event.target.value;

    if (!/^\d*(?:\.\d*)?$/.test(nextValue)) {
      return;
    }

    setQuantityKg(nextValue);
    setValidationMessage('');
  }

  function handleSacksChange(event) {
    const nextValue = event.target.value;

    if (!/^\d*$/.test(nextValue)) {
      return;
    }

    setQuantitySacks(nextValue);
    setValidationMessage('');
  }

  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.62)]"
      contentClassName="!items-center"
      zIndexClassName="z-[60]"
    >
      <form
        className="mx-auto w-full max-w-4xl rounded-[22px] border border-[var(--theme-border)] bg-white p-5 shadow-[0_24px_70px_rgba(var(--theme-shadow-rgb),0.34)]"
        onSubmit={handleSubmit}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <strong className="block text-[1.02rem] text-[var(--theme-text)]">
                Dumping Inspection
              </strong>
              <DumpingLockBadge item={item} />
            </div>
            <p className="m-0 mt-1 text-[0.86rem] text-[var(--theme-text-muted)]">
              {formatPurchaseOrderCode(item)} - {formatSupplierName(item)}
            </p>
          </div>
          <button
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Close dumping inspection"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <MergeSummaryTile label="Original KG" value={formatKgNet(getOriginalQuantityKg(item))} />
          <MergeSummaryTile label="Net Adjustment" value={formatKgNet(existingAdjustmentKg)} />
          <MergeSummaryTile label="Available KG" value={formatKgNet(availableKg)} />
          {canShowSacksDetails ? (
            <>
              <MergeSummaryTile
                label="Original Sacks"
                value={formatQuantity(originalSacks, 'sacks')}
              />
              <MergeSummaryTile
                label="Sacks Adjustment"
                value={formatQuantity(existingAdjustmentSacks, 'sacks')}
              />
              <MergeSummaryTile
                label="Available Sacks"
                value={formatQuantity(availableSacks, 'sacks')}
              />
            </>
          ) : null}
        </div>

        {canAdjustInventory ? (
          <>
            <div
              className={`mt-4 grid gap-3 ${
                shouldShowSacksInput
                  ? 'sm:grid-cols-[minmax(0,1fr)_150px_150px]'
                  : 'sm:grid-cols-[minmax(0,1fr)_180px]'
              }`}
            >
              <div className="grid min-w-0 gap-1.5">
                <CustomSelect
                  label="Reason"
                  name="reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  options={DUMPING_ADJUSTMENT_REASON_OPTIONS}
                  disabled={isLocked || isSubmitting}
                  searchable={false}
                />
              </div>
              <label className="grid min-w-0 gap-1.5">
                <span className="text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
                  KG
                </span>
                <input
                  className="min-h-[40px] w-full rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.84rem] text-[var(--theme-text-strong)] outline-none transition focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[#e7ddd4]"
                  type="text"
                  inputMode="decimal"
                  value={quantityKg}
                  onChange={handleQuantityChange}
                  placeholder="0.00"
                  disabled={isLocked || isSubmitting}
                />
              </label>
              {shouldShowSacksInput ? (
                <label className="grid min-w-0 gap-1.5">
                  <span className="text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
                    Sacks
                  </span>
                  <input
                    className="min-h-[40px] w-full rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.84rem] text-[var(--theme-text-strong)] outline-none transition focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[#e7ddd4]"
                    type="text"
                    inputMode="numeric"
                    value={quantitySacks}
                    onChange={handleSacksChange}
                    placeholder="0"
                    disabled={!canAdjustSacks || isLocked || isSubmitting}
                  />
                </label>
              ) : null}
            </div>

            <label className="mt-3 grid gap-1.5">
              <span className="text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
                Notes
              </span>
              <textarea
                className="min-h-[92px] resize-y rounded-[14px] border border-[var(--theme-border)] bg-white px-3 py-2 text-[0.92rem] text-[var(--theme-text-strong)] outline-none transition focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[#e7ddd4]"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Optional inspection notes"
                disabled={isLocked || isSubmitting}
              />
            </label>
          </>
        ) : (
          <div className="mt-4 rounded-[14px] border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-4 py-3 text-[0.86rem] font-semibold text-[var(--theme-text-subtle)]">
            Merged inventory cannot be adjusted in Dumping. Please apply dumping adjustments to the source P.O.s before merging.
          </div>
        )}

        {dumpingAdjustments.length ? (
          <div className="mt-4 rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <strong className="block text-[0.84rem] text-[var(--theme-text-strong)]">
                Previous Adjustments
              </strong>
              {shouldPaginateAdjustments ? (
                <span className="text-[0.76rem] font-semibold text-[var(--theme-text-muted)]">
                  Page {normalizedAdjustmentsPage} of {adjustmentsTotalPages}
                </span>
              ) : null}
            </div>
            <div className="mt-2 grid gap-2">
              {visibleDumpingAdjustments.map((adjustment) => (
                <div
                  key={adjustment.id}
                  className={`grid items-center gap-2 rounded-[12px] bg-white px-3 py-2 text-[0.82rem] text-[var(--theme-text-subtle)] ${
                    canShowSacksDetails
                      ? 'sm:grid-cols-[150px_100px_100px_minmax(0,1fr)_36px]'
                      : 'sm:grid-cols-[160px_100px_minmax(0,1fr)_36px]'
                  }`}
                >
                  <span className="font-semibold text-[var(--theme-text-strong)]">
                    {adjustment.reason}
                    {adjustment.isCarriedFromMergeSource && adjustment.sourcePoCode ? (
                      <span className="block text-[0.7rem] font-semibold text-[#9a8d82]">
                        {adjustment.sourcePoCode}
                      </span>
                    ) : null}
                  </span>
                  <span>{formatKgNet(adjustment.quantityKg)}</span>
                  {canShowSacksDetails ? (
                    <span>{formatQuantity(adjustment.quantitySacks, 'sacks')}</span>
                  ) : null}
                  <span className="truncate" title={adjustment.notes}>{adjustment.notes || '-'}</span>
                  <button
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#efd2c9] bg-[#fff7f4] text-[#b54b38] transition hover:border-[#d9a99b] hover:bg-[#ffede8] disabled:cursor-not-allowed disabled:opacity-50"
                    type="button"
                    onClick={() => onDeleteAdjustment(adjustment.id)}
                    disabled={
                      isLocked ||
                      isSubmitting ||
                      Boolean(deletingAdjustmentId) ||
                      isMergedInventory ||
                      Boolean(adjustment.isCarriedFromMergeSource)
                    }
                    aria-label={`Delete ${adjustment.reason} dumping adjustment`}
                    title={
                      adjustment.isCarriedFromMergeSource
                        ? 'Carried from source P.O.'
                        : isMergedInventory
                          ? 'Merged inventory adjustments are disabled'
                        : 'Delete adjustment'
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
            {shouldPaginateAdjustments ? (
              <div className="mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-[var(--theme-border-soft)] pt-3">
                <button
                  className="inline-flex min-h-[34px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                  onClick={() => setAdjustmentsPage((page) => Math.max(page - 1, 1))}
                  disabled={normalizedAdjustmentsPage <= 1}
                >
                  Prev
                </button>
                <button
                  className="inline-flex min-h-[34px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                  onClick={() =>
                    setAdjustmentsPage((page) => Math.min(page + 1, adjustmentsTotalPages))
                  }
                  disabled={normalizedAdjustmentsPage >= adjustmentsTotalPages}
                >
                  Next
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {validationMessage ? (
          <div className="mt-4 rounded-[14px] border border-[#f1c7bc] bg-[#fff4f1] px-4 py-3 text-[0.86rem] font-semibold text-[#b54b38]">
            {validationMessage}
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {canMarkDry ? (
              <button
                className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-full border border-[var(--theme-accent)] bg-white px-5 text-[0.86rem] font-semibold text-[var(--theme-accent-strong)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-50"
                type="button"
                onClick={onOpenMarkDryConfirm}
                disabled={isSubmitting || isMarkingDry}
              >
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                {isMarkingDry ? 'Updating...' : 'Mark as Dry'}
              </button>
            ) : null}
            <button
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-full border border-[var(--theme-accent)] bg-white px-5 text-[0.86rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-50"
              type="button"
              onClick={onOpenLockConfirm}
              disabled={isLocked || isSubmitting || isLocking}
            >
              <Lock className="h-4 w-4" aria-hidden="true" />
              {isLocked ? 'Locked' : 'Lock Inventory'}
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-5 text-[0.86rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            {canAdjustInventory ? (
              <button
                className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-full border border-[var(--theme-accent)] bg-[var(--theme-accent)] px-5 text-[0.86rem] font-semibold text-white shadow-[0_10px_20px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
                type="submit"
                disabled={isLocked || isSubmitting || availableKg <= 0}
              >
                <Scale className="h-4 w-4" aria-hidden="true" />
                {isSubmitting ? 'Saving...' : 'Save Adjustment'}
              </button>
            ) : null}
          </div>
        </div>
      </form>
    </ModalBackdrop>
  );
}

function DumpingLockConfirmationModal({ item, isSubmitting, onCancel, onConfirm }) {
  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.62)]"
      contentClassName="!items-center"
      zIndexClassName="z-[70]"
    >
      <div className="mx-auto w-full max-w-lg rounded-[20px] border border-[var(--theme-border)] bg-white p-5 shadow-[0_24px_70px_rgba(var(--theme-shadow-rgb),0.34)]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <strong className="block text-[1rem] text-[var(--theme-text)]">
              Lock dumping inventory?
            </strong>
            <p className="m-0 mt-2 text-[0.86rem] leading-5 text-[var(--theme-text-subtle)]">
              This will send {formatPurchaseOrderCode(item)} to Inventory/Releasing and stop further dumping adjustments for this record.
            </p>
          </div>
          <button
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Cancel lock confirmation"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-4 grid gap-2 rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3 text-[0.84rem] text-[var(--theme-text-subtle)]">
          <CalculationLine label="Source" value={formatPurchaseOrderCode(item)} />
          <CalculationLine label="Sellable KG" value={formatKgNet(item.quantityKg)} />
          <CalculationLine label="Net Adjustment" value={formatKgNet(item.dumpingAdjustmentKg)} />
          <CalculationLine label="Sacks Adjustment" value={formatSacksAdjustment(item)} />
        </div>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-5 text-[0.86rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-full border border-[var(--theme-accent)] bg-[var(--theme-accent)] px-5 text-[0.86rem] font-semibold text-white shadow-[0_10px_20px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            <Lock className="h-4 w-4" aria-hidden="true" />
            {isSubmitting ? 'Locking...' : 'Confirm Lock'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function MarkDryConfirmationModal({ item, isSubmitting, onCancel, onConfirm }) {
  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.62)]"
      contentClassName="!items-center"
      zIndexClassName="z-[70]"
    >
      <div className="mx-auto w-full max-w-lg rounded-[20px] border border-[var(--theme-border)] bg-white p-5 shadow-[0_24px_70px_rgba(var(--theme-shadow-rgb),0.34)]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <strong className="block text-[1rem] text-[var(--theme-text)]">
              Mark wet batch as dry?
            </strong>
            <p className="m-0 mt-2 text-[0.86rem] leading-5 text-[var(--theme-text-subtle)]">
              This will convert {formatPurchaseOrderCode(item)} to a dry dumping record so it can be selected for merge.
            </p>
          </div>
          <button
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Cancel mark as dry confirmation"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-4 grid gap-2 rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3 text-[0.84rem] text-[var(--theme-text-subtle)]">
          <CalculationLine label="Source" value={formatPurchaseOrderCode(item)} />
          <CalculationLine label="Current Batch Type" value={item.category || '-'} />
          <CalculationLine label="Sellable KG" value={formatKgNet(item.quantityKg)} />
          <CalculationLine label="Net Adjustment" value={formatKgNet(item.dumpingAdjustmentKg)} />
        </div>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-5 text-[0.86rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-full border border-[var(--theme-accent)] bg-[var(--theme-accent)] px-5 text-[0.86rem] font-semibold text-white shadow-[0_10px_20px_rgba(var(--theme-shadow-rgb),0.18)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border-[var(--theme-border-soft)] disabled:bg-[var(--theme-surface-soft)] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            {isSubmitting ? 'Updating...' : 'Confirm Dry'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function CalculationLine({ label, value }) {
  return (
    <div className="grid gap-1 rounded-[12px] bg-[var(--theme-surface-soft)] px-3 py-2 sm:grid-cols-[150px_minmax(0,1fr)]">
      <span className="font-semibold text-[var(--theme-text-muted)]">{label}</span>
      <span className="font-medium text-[var(--theme-text-strong)]">{value}</span>
    </div>
  );
}

function MergeSummaryTile({ label, value }) {
  return (
    <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
      <span className="block text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
        {label}
      </span>
      <strong className="mt-1 block break-words text-[0.98rem] text-[var(--theme-text-strong)]">
        {value}
      </strong>
    </div>
  );
}

function ReceiptImagePreviewModal({ image, onClose }) {
  const [zoom, setZoom] = useState(1);
  const [now, setNow] = useState(() => new Date());
  const zoomPercent = Math.round(zoom * 100);
  const receiptFields = parseReceiptDetails(image.details);
  const isMergedInventory = Boolean(image.item?.isMergedInventory);
  const shouldShowZoomControls = Boolean(image.url);
  const receiptImageRetentionDays = getReceiptImageRetentionDays(image.item);
  const receiptImageRetention = getReceiptImageRetention(image.item, now);

  useEffect(() => {
    if (!image.item?.imageExpiresAt) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [image.item?.imageExpiresAt]);

  function handleZoomOut() {
    setZoom((currentZoom) => Math.max(0.5, Math.round((currentZoom - 0.25) * 100) / 100));
  }

  function handleZoomIn() {
    setZoom((currentZoom) => Math.min(3, Math.round((currentZoom + 0.25) * 100) / 100));
  }

  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.55)]"
      contentClassName="!px-4"
      zIndexClassName="z-50"
    >
      <div className="mx-auto w-full max-w-5xl rounded-[22px] border border-[var(--theme-border)] bg-white p-3 shadow-[0_24px_70px_rgba(var(--theme-shadow-rgb),0.32)]">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3 px-1">
          <strong className="text-[0.95rem] text-[var(--theme-text)]">
            {image.title || 'Receipt image'}
          </strong>
          <div className="flex items-center gap-2">
            {shouldShowZoomControls ? (
              <div className="inline-flex items-center overflow-hidden rounded-full border border-[var(--theme-border)] bg-white">
                <button
                  className="inline-flex h-9 w-9 items-center justify-center text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:text-[var(--theme-text-muted)]"
                  type="button"
                  onClick={handleZoomOut}
                  disabled={zoom <= 0.5}
                  aria-label="Zoom out receipt image"
                  title="Zoom out"
                >
                  <Minus className="h-4 w-4" aria-hidden="true" />
                </button>
                <span className="min-w-[54px] border-x border-[var(--theme-border-soft)] px-2 text-center text-[0.78rem] font-semibold text-[var(--theme-text-subtle)]">
                  {zoomPercent}%
                </span>
                <button
                  className="inline-flex h-9 w-9 items-center justify-center text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:text-[var(--theme-text-muted)]"
                  type="button"
                  onClick={handleZoomIn}
                  disabled={zoom >= 3}
                  aria-label="Zoom in receipt image"
                  title="Zoom in"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            ) : null}
            <button
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={onClose}
              aria-label="Close receipt image preview"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="grid max-h-[78vh] gap-3 overflow-y-auto">
          {isMergedInventory ? (
            <MergedInventoryDetails item={image.item} />
          ) : null}
          {image.url ? (
            <div className="relative max-h-[58vh] overflow-auto rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] p-2">
              <ReceiptImageRetentionBadge retention={receiptImageRetention} />
              <img
                className="mx-auto block h-auto max-w-none rounded-[12px] object-contain"
                src={image.url}
                alt={`${image.title || 'Receipt'} full preview`}
                style={{ width: `${zoom * 100}%` }}
              />
            </div>
          ) : image.details && !isMergedInventory ? (
            <ReceiptImageExpiredNotice retentionDays={receiptImageRetentionDays} />
          ) : null}
          {image.details && !isMergedInventory ? (
            <div className="rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
              <strong className="block text-[0.86rem] text-[var(--theme-text-strong)]">
                Receipt Details
              </strong>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white p-3">
                  <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
                    Captured Fields
                  </strong>
                  <div className="mt-2 grid gap-1.5 text-[0.8rem] text-[var(--theme-text-subtle)]">
                    {receiptFields.length ? (
                      receiptFields.map((field) => (
                        <div
                          key={`${field.label}-${field.value}`}
                          className="grid grid-cols-[112px_minmax(0,1fr)] gap-2"
                        >
                          <span className="font-semibold text-[var(--theme-text-muted)]">{field.label}</span>
                          <span className="break-words font-medium text-[var(--theme-text-strong)]">
                            {field.value || '-'}
                          </span>
                        </div>
                      ))
                    ) : (
                      <pre className="m-0 max-h-56 overflow-y-auto whitespace-pre-wrap text-[0.8rem] leading-5 text-[var(--theme-text-subtle)]">
                        {image.details}
                      </pre>
                    )}
                  </div>
                </div>

                <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white p-3">
                  <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
                    Remarks
                  </strong>
                  <p className="m-0 mt-2 whitespace-pre-wrap text-[0.84rem] leading-5 text-[var(--theme-text-subtle)]">
                    {image.remarks || '-'}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </ModalBackdrop>
  );
}

function MergedInventoryDetails({ item }) {
  const [selectedSourceDetails, setSelectedSourceDetails] = useState(null);
  const sourceDetailsRef = useRef(null);
  const sources = Array.isArray(item.sourcePurchaseOrders) ? item.sourcePurchaseOrders : [];

  useEffect(() => {
    if (!selectedSourceDetails) {
      return;
    }

    window.requestAnimationFrame(() => {
      sourceDetailsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    });
  }, [selectedSourceDetails]);

  return (
    <div className="rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
      <strong className="block text-[0.86rem] text-[var(--theme-text-strong)]">
        Merged Inventory Details
      </strong>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <MergeSummaryTile label="Warehouse" value={formatWarehouseLocation(item)} />
        <MergeSummaryTile label="Sacks Mark" value={formatSacksMarkDisplay(item)} />
        <MergeSummaryTile label="MC %" value={formatMoistureContent(item.moistureContent)} />
        <MergeSummaryTile label="Total Quantity" value={formatInventoryQuantity(item, 'quantity')} />
        <MergeSummaryTile label="KG Net" value={formatKgNet(item.quantityKg)} />
      </div>

      <div className="mt-4 overflow-hidden rounded-[14px] border border-[var(--theme-border-soft)] bg-white">
        <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] border-collapse">
            <thead>
              <tr className="bg-[var(--theme-accent-panel)]">
                <TableHeading>Source P.O.</TableHeading>
                <TableHeading>Sacks Mark</TableHeading>
                <TableHeading>Sacks</TableHeading>
                <TableHeading>MC %</TableHeading>
                <TableHeading>KG Net</TableHeading>
                <TableHeading>View Details</TableHeading>
              </tr>
            </thead>
            <tbody>
              {sources.length ? (
                sources.map((source) => {
                  const sourceDetails = splitReceivingNote(source.remarks);
                  const hasDetails = Boolean(
                    sourceDetails.remarks || sourceDetails.receiptDetails || source.image
                  );

                  return (
                    <tr key={`${item.id}-${source.purchaseOrderId}`} className="border-t border-[var(--theme-border-soft)]">
                      <TableCell>{source.poCode || '-'}</TableCell>
                      <TableCell>
                        <span className="block max-w-[190px] truncate" title={formatSourceSacksMark(source)}>
                          {formatSourceSacksMark(source)}
                        </span>
                      </TableCell>
                      <TableCell>{formatQuantity(source.totalQuantity, source.quantityUnit)}</TableCell>
                      <TableCell>{formatMoistureContent(source.moistureContent)}</TableCell>
                      <TableCell>{formatKgNet(source.kgNet)}</TableCell>
                      <TableCell>
                        <button
                          className="inline-flex min-h-[32px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.76rem] font-semibold text-[var(--theme-text-subtle)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-50"
                          type="button"
                          disabled={!hasDetails}
                          onClick={() => setSelectedSourceDetails(source)}
                        >
                          View Details
                        </button>
                      </TableCell>
                    </tr>
                  );
                })
              ) : (
                <tr className="border-t border-[var(--theme-border-soft)]">
                  <td className="px-4 py-8 text-center text-[0.84rem] text-[var(--theme-text-muted)]" colSpan={6}>
                    No source P.O. details found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedSourceDetails ? (
        <div ref={sourceDetailsRef}>
          <MergedSourceDetailsPanel
            source={selectedSourceDetails}
            onClose={() => setSelectedSourceDetails(null)}
          />
        </div>
      ) : null}

    </div>
  );
}

function MergedSourceDetailsPanel({ source, onClose }) {
  const note = splitReceivingNote(source.remarks);
  const receiptFields = parseReceiptDetails(note.receiptDetails);
  const receiptImageRetentionDays = getReceiptImageRetentionDays(source);
  const receiptImageRetention = getReceiptImageRetention(source);

  return (
    <div className="mt-4 rounded-[14px] border border-[var(--theme-border-soft)] bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <strong className="block text-[0.9rem] text-[var(--theme-text-strong)]">
            {source.poCode || 'Source P.O.'} Details
          </strong>
          <span className="mt-0.5 block text-[0.78rem] text-[var(--theme-text-muted)]">
            {source.supplierLabel || '-'}{source.receivedAt ? ` - ${formatShortDate(source.receivedAt)}` : ''}
          </span>
        </div>
        <button
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
          type="button"
          onClick={onClose}
          aria-label="Close source details"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div className="rounded-[12px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
          <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
            Receipt Details
          </strong>
          <div className="mt-2 grid gap-1.5 text-[0.8rem] text-[var(--theme-text-subtle)]">
            {receiptFields.length ? (
              receiptFields.map((field) => (
                <div
                  key={`${source.poCode}-${field.label}-${field.value}`}
                  className="grid grid-cols-[112px_minmax(0,1fr)] gap-2"
                >
                  <span className="font-semibold text-[var(--theme-text-muted)]">{field.label}</span>
                  <span className="break-words font-medium text-[var(--theme-text-strong)]">
                    {field.value || '-'}
                  </span>
                </div>
              ))
            ) : (
              <span className="text-[var(--theme-text-muted)]">No captured receipt fields found.</span>
            )}
          </div>
        </div>

        <div className="rounded-[12px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
          <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
            Remarks
          </strong>
          <p className="m-0 mt-2 whitespace-pre-wrap text-[0.84rem] leading-5 text-[var(--theme-text-subtle)]">
            {note.remarks || '-'}
          </p>
          {source.image ? (
            <div className="mt-3 grid justify-start gap-2">
              <ReceiptImageRetentionBadge
                className="static ml-0"
                retention={receiptImageRetention}
              />
              <a
                className="inline-flex text-[0.78rem] font-semibold text-[var(--theme-accent)] underline-offset-2 hover:text-[var(--theme-accent-strong)] hover:underline"
                href={buildAssetUrl(source.image)}
                target="_blank"
                rel="noreferrer"
              >
                Open receipt image
              </a>
            </div>
          ) : note.receiptDetails ? (
            <ReceiptImageExpiredNotice
              className="mt-3"
              retentionDays={receiptImageRetentionDays}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ReceiptImageRetentionBadge({ retention, className = '' }) {
  if (!retention?.label) {
    return null;
  }

  return (
    <span
      className={`sticky right-3 top-3 z-10 ml-auto flex w-max items-center rounded-full border px-3 py-1.5 text-[0.76rem] font-semibold shadow-[0_8px_22px_rgba(var(--theme-shadow-rgb),0.12)] ${
        retention.tone === 'danger'
          ? 'border-[#efc3b8] bg-[#fff6f3] text-[#a34334]'
          : retention.tone === 'warning'
            ? 'border-[#ead7a8] bg-[#fff8e6] text-[#8a6424]'
            : 'border-[#d6e6d7] bg-[#f4faf4] text-[#3f7545]'
      } ${className}`}
    >
      {retention.modalLabel || retention.label}
    </span>
  );
}

function ReceiptImageExpiredNotice({ retentionDays, className = '' }) {
  return (
    <div
      className={`rounded-[14px] border border-[#ead7a8] bg-[#fff8e6] px-3 py-2 text-[0.8rem] font-semibold text-[#8a6424] ${className}`}
    >
      Receipt image self-destructed after {retentionDays} days
    </div>
  );
}

function getReceiptImageRetentionDays(item) {
  return Number(item?.imageRetentionDays || 30);
}

function getReceiptImageRetention(item, now = new Date()) {
  const retentionDays = Number(item?.imageRetentionDays || 30);
  const daysRemaining = Number(item?.imageDaysRemaining);
  const expiresAt = item?.imageExpiresAt ? new Date(item.imageExpiresAt) : null;
  const remainingMs =
    expiresAt && !Number.isNaN(expiresAt.getTime())
      ? expiresAt.getTime() - now.getTime()
      : null;

  if (!item?.image || (!Number.isFinite(daysRemaining) && remainingMs === null)) {
    return {
      daysRemaining: null,
      retentionDays,
      label: '',
      tone: 'neutral',
    };
  }

  const normalizedDaysRemaining =
    remainingMs === null
      ? Math.max(0, Math.min(retentionDays, daysRemaining))
      : Math.max(0, Math.min(retentionDays, Math.ceil(remainingMs / MS_PER_DAY)));
  const countdownLabel = formatSelfDestructCountdown(remainingMs);

  return {
    daysRemaining: normalizedDaysRemaining,
    retentionDays,
    label:
      countdownLabel ||
      (normalizedDaysRemaining <= 0
        ? 'Deletes today'
        : `${normalizedDaysRemaining} day${normalizedDaysRemaining === 1 ? '' : 's'} left`),
    modalLabel:
      countdownLabel
        ? remainingMs <= 0
          ? countdownLabel
          : `${countdownLabel} to self-destruct`
        : normalizedDaysRemaining <= 0
          ? 'Self-destructs today'
          : `${normalizedDaysRemaining} day${normalizedDaysRemaining === 1 ? '' : 's'} left to self-destruct`,
    tone:
      normalizedDaysRemaining <= 3
        ? 'danger'
        : normalizedDaysRemaining <= 7
          ? 'warning'
          : 'success',
  };
}

function formatSelfDestructCountdown(remainingMs) {
  if (!Number.isFinite(remainingMs)) {
    return '';
  }

  if (remainingMs <= 0) {
    return 'Self-destructing now';
  }

  if (remainingMs >= MS_PER_DAY) {
    return '';
  }

  const totalSeconds = Math.ceil(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];

  if (hours) {
    parts.push(`${hours}h`);
  }

  if (hours || minutes) {
    parts.push(`${minutes}m`);
  }

  parts.push(`${seconds}s`);

  return `${parts.join(' ')} left`;
}

function TableHeading({ children }) {
  return (
    <th className="whitespace-nowrap px-3 py-2.5 text-left text-[0.72rem] font-semibold uppercase tracking-[0.04em] text-[var(--theme-accent-panel-text)] lg:px-4">
      {children}
    </th>
  );
}

function TableCell({ children }) {
  return (
    <td className="whitespace-nowrap px-3 py-2 align-middle text-[0.84rem] text-[var(--theme-text)] lg:px-4">
      {children}
    </td>
  );
}

function RecordMeta({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[var(--theme-text-muted)]">{label}</span>
      <div className="text-right font-medium text-[var(--theme-text)]">{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center text-center text-[var(--theme-text-muted)]">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-text-muted)]">
        <ClipboardList className="h-6 w-6" aria-hidden="true" />
      </div>
      <strong className="mt-4 text-[1rem] font-medium text-[var(--theme-text-strong)]">
        No records available for dumping
      </strong>
    </div>
  );
}

function filterInventoryItems(items, filters) {
  const normalizedSearch = String(filters.search || '').trim().toLowerCase();

  return items.filter((item) => {
    if (isEmptyWetBatch(item)) {
      return false;
    }

    const note = splitReceivingNote(item.remarks);
    const matchesSearch =
      !normalizedSearch ||
      [
        formatPurchaseOrderCode(item),
        ...getMergedSourcePoCodes(item),
        item.companyName,
        item.supplierName,
        item.sacksMark,
        item.warehouseLocation,
        item.productName,
        item.category,
        item.moistureContent,
        formatInventoryQuantity(item, 'dryQuantity'),
        formatInventoryQuantity(item, 'wetQuantity'),
        formatInventoryQuantity(item, 'quantity'),
        formatKgNet(item.quantityKg),
        note.remarks,
        note.receiptDetails,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch));
    const matchesDate =
      !filters.date || toDateInputValue(item.receivedAt) === filters.date;
    const matchesWarehouse =
      filters.warehouseLocation === 'All' ||
      String(item.warehouseLocation || '').trim() === String(filters.warehouseLocation || '').trim();
    return matchesSearch && matchesDate && matchesWarehouse;
  });
}

function isEmptyWetBatch(item) {
  return (
    String(item.category || '').toLowerCase() === 'wet' &&
    Number(item.wetQuantity || item.quantity || item.quantityKg || 0) <= 0
  );
}

function buildWarehouseOptions(items) {
  const warehouseLocations = Array.from(
    new Set(
      items
        .map((item) => String(item.warehouseLocation || '').trim())
        .filter(Boolean)
    )
  ).sort((firstValue, secondValue) => firstValue.localeCompare(secondValue));

  return [
    { value: 'All', label: 'All Warehouses' },
    ...warehouseLocations.map((warehouseLocation) => ({
      value: warehouseLocation,
      label: warehouseLocation,
    })),
  ];
}

function buildSupplierStockBreakdown(items) {
  const stocksBySacksMark = new Map();

  items.forEach((item) => {
    const sacksMark = formatSacksMarkDisplay(item);
    const sacksMarkKey = sacksMark.toLowerCase();
    const currentStock = stocksBySacksMark.get(sacksMarkKey) || {
      id: sacksMarkKey,
      sacksMark,
      quantity: 0,
      quantityUnit: item.quantityUnit || 'sacks',
      quantityKg: 0,
    };

    currentStock.quantity += getInventoryQuantityValue(item);
    currentStock.quantityKg += Number(item.quantityKg || 0);
    stocksBySacksMark.set(sacksMarkKey, currentStock);
  });

  return Array.from(stocksBySacksMark.values())
    .filter((item) => item.quantityKg > 0)
    .sort(
      (firstItem, secondItem) =>
        secondItem.quantityKg - firstItem.quantityKg ||
        firstItem.sacksMark.localeCompare(secondItem.sacksMark)
    );
}

function getMergedSourceSupplierSuggestions(item) {
  if (!item.isMergedInventory || !Array.isArray(item.sourcePurchaseOrders)) {
    return [];
  }

  return item.sourcePurchaseOrders.map((source) => [
    'supplier',
    source.supplierLabel || source.supplierName,
    `Source Supplier in ${formatPurchaseOrderCode(item)}`,
    {
      supplierId: source.supplierId,
      supplierName: source.supplierLabel || source.supplierName,
    },
  ]);
}

function buildInventorySearchSuggestions(items) {
  const suggestionsByKey = new Map();

  items.forEach((item) => {
    [
      ['po-code', formatPurchaseOrderCode(item), 'P.O. Code'],
      ...getMergedSourcePoCodes(item).map((poCode) => [
        'source-po-code',
        poCode,
        `Source P.O. merged in ${formatPurchaseOrderCode(item)}`,
      ]),
      [
        'supplier',
        item.companyName || item.supplierName,
        'Supplier',
        {
          supplierId: item.supplierId,
          supplierName: item.companyName || item.supplierName,
        },
      ],
      ...getMergedSourceSupplierSuggestions(item),
      ['warehouse', item.warehouseLocation, 'Warehouse'],
    ].forEach(([type, value, detail, metadata = {}]) => {
      const normalizedValue = String(value || '').trim();

      if (!normalizedValue) {
        return;
      }

      suggestionsByKey.set(`${type}:${normalizedValue.toLowerCase()}`, {
        group: type,
        value: normalizedValue,
        label: normalizedValue,
        detail,
        ...metadata,
      });
    });
  });

  return Array.from(suggestionsByKey.values())
    .sort(sortInventorySearchSuggestions);
}

function getDumpingStageItems(items = []) {
  return items.filter(
    (item) =>
      !item.isMergedInventory &&
      !item.isDumpingLocked &&
      String(item.category || '').trim() === 'Wet'
  );
}

function sortInventorySearchSuggestions(firstSuggestion, secondSuggestion) {
  const groupPriority = {
    'source-po-code': 0,
    'po-code': 1,
    supplier: 2,
    warehouse: 3,
  };
  const firstPriority = groupPriority[firstSuggestion.group] ?? 99;
  const secondPriority = groupPriority[secondSuggestion.group] ?? 99;

  return (
    firstPriority - secondPriority ||
    firstSuggestion.label.localeCompare(secondSuggestion.label)
  );
}

function getInventorySelectionId(item) {
  return String(item.sourceKey || item.id || item.purchaseOrderId);
}

function getInventoryMergeEligibility(item, selectedItems) {
  if (item.isMergedInventory) {
    return {
      canSelect: false,
      reason: 'Merged inventory is already consolidated',
      shortReason: 'Merged',
    };
  }

  if (item.isDumpingLocked) {
    return {
      canSelect: false,
      reason: 'This inventory record is locked from Dumping',
      shortReason: 'Locked',
    };
  }

  if (item.category !== 'Wet') {
    return {
      canSelect: false,
      reason: 'Only wet dumping records can be merged',
      shortReason: 'Type',
    };
  }

  if (!selectedItems.length) {
    return {
      canSelect: true,
      reason: '',
      shortReason: '',
    };
  }

  const baseItem = selectedItems[0];
  const itemWarehouse = normalizeMergeValue(item.warehouseLocation);
  const baseWarehouse = normalizeMergeValue(baseItem.warehouseLocation);

  if (itemWarehouse !== baseWarehouse) {
    return {
      canSelect: false,
      reason: 'Different warehouse location',
      shortReason: 'Warehouse',
    };
  }

  return {
    canSelect: true,
    reason: '',
    shortReason: '',
  };
}

function buildMergedInventorySummary(items) {
  if (!items.length) {
    return null;
  }

  const totalDry = sumNumericField(items, 'dryQuantity');
  const totalWet = sumNumericField(items, 'wetQuantity');
  const totalQuantity = sumNumericField(items, 'quantity');
  const totalKgNet = sumNumericField(items, 'quantityKg');
  const stockUnit = items[0].quantityUnit || 'kg';
  const hasMixedUnits = hasMixedQuantityUnits(items);
  const weightedMoisture = calculateWeightedMoisture(items, totalKgNet);

  return {
    warehouseLabel: formatWarehouseLocation(items[0]),
    stockUnit,
    quantityBasisLabel: hasMixedUnits ? 'KG net basis' : `${stockUnit === 'kg' ? 'kg' : 'sacks'} stock`,
    supplierLabel: getMergedSacksMarkLabel(items),
    moistureLabel:
      weightedMoisture === null ? 'Mixed' : `${formatNumber(weightedMoisture)}%`,
    dryLabel: hasMixedUnits ? 'Mixed units' : formatQuantity(totalDry, stockUnit),
    wetLabel: hasMixedUnits ? 'Mixed units' : formatQuantity(totalWet, stockUnit),
    totalQuantityLabel: hasMixedUnits ? 'Mixed units' : formatQuantity(totalQuantity, stockUnit),
    totalDry,
    totalWet,
    totalQuantity,
    totalKgNet,
  };
}

function getMergeSacksMarkOptions(items = []) {
  const marks = [];
  const seenMarks = new Set();

  items.forEach((item) => {
    const normalizedMark = String(item.sacksMark || '').trim();
    const key = normalizedMark.toLowerCase();

    if (!normalizedMark || seenMarks.has(key)) {
      return;
    }

    seenMarks.add(key);
    marks.push({
      value: normalizedMark,
      label: normalizedMark,
    });
  });

  return [
    ...marks,
    {
      value: MERGE_SACKS_MARK_OTHER,
      label: 'Others',
    },
  ];
}

function getSelectedMergeSacksMark(choice, customValue) {
  return String(choice === MERGE_SACKS_MARK_OTHER ? customValue : choice || '')
    .trim()
    .toUpperCase()
    .slice(0, 120);
}

function hasMixedQuantityUnits(items) {
  const quantityUnits = new Set(
    items.map((item) => normalizeMergeValue(item.quantityUnit || 'kg'))
  );

  return quantityUnits.size > 1;
}

function getMergedSacksMarkLabel(items) {
  const sacksMarkLabels = new Set(
    items.map((item) =>
      normalizeMergeValue(formatSacksMarkDisplay(item))
    )
  );

  if (sacksMarkLabels.size === 1) {
    return formatSacksMarkDisplay(items[0]);
  }

  return 'Mixed Sacks Marks';
}

function calculateWeightedMoisture(items, totalKgNet) {
  if (totalKgNet <= 0) {
    return null;
  }

  const weightedTotal = items.reduce((total, item) => {
    const moistureValue = parseMoistureContent(item.moistureContent);

    if (moistureValue === null) {
      return null;
    }

    if (total === null) {
      return null;
    }

    return total + Number(item.quantityKg || 0) * moistureValue;
  }, 0);

  return weightedTotal === null ? null : weightedTotal / totalKgNet;
}

function parseMoistureContent(value) {
  const normalizedValue = String(value || '').trim();

  if (!normalizedValue) {
    return null;
  }

  const numberValue = Number(normalizedValue.replace('%', ''));

  return Number.isFinite(numberValue) ? numberValue : null;
}

function formatMoistureContent(value) {
  const moistureValue = parseMoistureContent(value);

  if (moistureValue === null) {
    return String(value || '').trim() || '-';
  }

  return `${formatNumber(moistureValue)}%`;
}

function sumNumericField(items, fieldName) {
  return items.reduce((total, item) => total + Number(item[fieldName] || 0), 0);
}

function normalizeMergeValue(value) {
  return String(value || '').trim().toLowerCase();
}

function formatPurchaseOrderCode(item) {
  if (item.poCode) {
    return item.poCode;
  }

  return item.poCode || `PO-${String(item.purchaseOrderId || '').padStart(4, '0')}`;
}

function getMergedSourcePoCodes(item) {
  if (!item.isMergedInventory || !Array.isArray(item.sourcePurchaseOrders)) {
    return [];
  }

  return Array.from(
    new Set(
      item.sourcePurchaseOrders
        .map((source) => String(source.poCode || '').trim())
        .filter(Boolean)
    )
  );
}

function formatSupplierName(item) {
  return String(item.companyName || item.supplierName || '').trim() || '-';
}

function formatSacksMarkDisplay(item) {
  return String(item.sacksMark || '').trim() || '-';
}

function formatSourceSacksMark(source) {
  const directSacksMark = String(source?.sacksMark || '').trim();

  if (directSacksMark) {
    return directSacksMark;
  }

  const supplierLabel = String(source?.supplierLabel || '').trim();
  const [sacksMark] = supplierLabel.split(' - ');
  return String(sacksMark || '').trim() || '-';
}

function formatWarehouseLocation(item) {
  return String(item.warehouseLocation || '').trim() || '-';
}

function getInventoryQuantityValue(item) {
  const label = String(item.quantityLabel || '').trim();
  const labelMatch = label.match(/[\d,.]+/);

  if (labelMatch) {
    return Number(labelMatch[0].replace(/,/g, ''));
  }

  return Number(item.quantity || 0);
}

function getOriginalQuantityKg(item) {
  return Number(item.originalQuantityKg ?? item.quantityKg ?? 0);
}

function getOriginalQuantity(item) {
  return Number(item.originalQuantity ?? item.quantity ?? 0);
}

function getDumpingSourceType(item) {
  return item.sourceType || (item.isMergedInventory ? 'merge' : 'po');
}

function getDumpingSourceId(item) {
  return Number(item.sourceId || (item.isMergedInventory ? item.mergeId : item.purchaseOrderId));
}

function refreshInspectionItem(currentItem, inventoryItems) {
  if (!currentItem) {
    return null;
  }

  return (
    inventoryItems.find(
      (item) =>
        getDumpingSourceType(item) === getDumpingSourceType(currentItem) &&
        getDumpingSourceId(item) === getDumpingSourceId(currentItem)
    ) || currentItem
  );
}

function formatQuantity(value, unit = 'kg') {
  return `${formatNumber(value)} ${unit === 'kg' ? 'kg' : 'sacks'}`;
}

function formatInventoryQuantity(item, fieldName) {
  const labelByFieldName = {
    dryQuantity: item.dryQuantityLabel,
    wetQuantity: item.wetQuantityLabel,
    quantity: item.quantityLabel,
  };
  const label = String(labelByFieldName[fieldName] || '').trim();

  if (label) {
    return label;
  }

  return formatQuantity(item[fieldName], item.quantityUnit);
}

function formatSacksAdjustment(item) {
  const quantitySacks = Number(item.dumpingAdjustmentSacks || 0);

  if (item.quantityUnit !== 'sacks' && quantitySacks <= 0) {
    return '-';
  }

  return formatQuantity(quantitySacks, 'sacks');
}

function formatKgNet(value) {
  return `${formatNumber(value)} kg`;
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function toDateInputValue(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function splitReceivingNote(value) {
  const note = String(value || '').trim();
  const receiptMarker = findReceiptMarker(note);

  if (receiptMarker === -1) {
    return {
      remarks: note,
      receiptDetails: '',
    };
  }

  return {
    remarks: note.slice(0, receiptMarker).trim(),
    receiptDetails: formatReceiptDetails(note.slice(receiptMarker).trim()),
  };
}

function parseReceiptDetails(value) {
  return String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separatorIndex = line.indexOf(':');

      if (separatorIndex === -1) {
        return null;
      }

      return {
        label: line.slice(0, separatorIndex).trim(),
        value: line.slice(separatorIndex + 1).trim(),
      };
    })
    .filter(Boolean);
}

function findReceiptMarker(value) {
  const match = value.match(
    /(P\.O\. Code:|Ticket No\.:|Customer:|Commodity:|Plate #:|Driver:|Weigher:|Inbound Wt kg:|In Bound Wt kg:|Inbound Date:|In Bound Date:|Outbound Wt kg:|Outbound:|Outbound Date:|Gross KG:|Tare KG:|Net KG:)/
  );

  return match ? match.index : -1;
}

function formatReceiptDetails(value) {
  return value.replace(
    /\s+(P\.O\. Code:|Ticket No\.:|Customer:|Commodity:|Plate #:|Driver:|Weigher:|Inbound Wt kg:|In Bound Wt kg:|Inbound Date:|In Bound Date:|Outbound Wt kg:|Outbound:|Outbound Date:|Gross KG:|Tare KG:|Net KG:)/g,
    '\n$1'
  );
}

export default DumpingPage;
